import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Plus, Eye, EyeOff, Trash2, Loader2, AlertCircle, Users, StickyNote, MessageSquare, AArrowUp } from 'lucide-react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { ActivitySlideProps } from '../types';
import { getDndBackend } from './dndBackend';
import { supabase } from '../../../../utils/supabase/client';
import { getStudentSession } from '../../../../utils/supabase/auth';
import {
  getSlideSubmissions,
  getSessionParticipants,
  serverUpsertSubmission,
  serverGetSlideSubmissions,
} from '../../../../utils/supabase/queries';
import { broadcastToSession, unsubscribeFromSession } from '../../../../utils/supabase/realtime';
import { stripHtml } from '../../../../utils/slides/stripHtml';

const RICH_TEXT_STYLES = `
  .rich-text-content h1 {
    font-size: 2em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h2 {
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h3 {
    font-size: 1.25em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content strong {
    font-weight: bold;
  }
  .rich-text-content em {
    font-style: italic;
  }
  .rich-text-content u {
    text-decoration: underline;
  }
  .rich-text-content ul {
    list-style-type: disc;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content ol {
    list-style-type: decimal;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content li {
    margin-bottom: 0.5em;
  }
  .rich-text-content p {
    margin-bottom: 0.5em;
  }
  .rich-text-content .ql-align-center {
    text-align: center;
  }
  .rich-text-content .ql-align-right {
    text-align: right;
  }
  .rich-text-content .ql-align-left {
    text-align: left;
  }
  .rich-text-content .ql-align-justify {
    text-align: justify;
  }
`;

interface StickyNote {
  id: string;
  content: string;
  author: string;
  color: string;
  position: { x: number; y: number };
  avatar: string;
}

interface CollaborateNoteAddedPayload {
  note: StickyNote;
}

interface CollaborateNoteDeletedPayload {
  noteId: string;
  authorStudentName: string;
}

interface CollaborateNoteMovedPayload {
  noteId: string;
  position: { x: number; y: number };
  authorStudentName: string;
}

type NoteFontSize = 'small' | 'medium' | 'large';

const NOTE_FONT_CLASSES: Record<NoteFontSize, string> = {
  small: 'text-sm leading-relaxed',
  medium: 'text-base leading-relaxed',
  large: 'text-lg leading-relaxed',
};



function DraggableStickyNote({
  note,
  onDelete,
  canDelete,
  canDrag,
  fontSize = 'small',
}: {
  note: StickyNote;
  onDelete: (id: string) => void;
  canDelete: boolean;
  canDrag: boolean;
  fontSize?: NoteFontSize;
}) {
  const [{ isDragging }, drag] = useDrag({
    type: 'NOTE',
    item: { id: note.id, left: note.position.x, top: note.position.y },
    canDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <motion.div
      ref={drag as any}
      initial={{ scale: 0, rotate: -10 }}
      animate={{ scale: 1, rotate: 0 }}
      style={{
        position: 'absolute',
        left: note.position.x,
        top: note.position.y,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="cursor-move group touch-none"
    >
      <div
        className={`w-64 p-4 rounded-2xl shadow-lg border-2 border-border hover:shadow-xl transition-shadow ${note.color}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-full ${note.avatar} flex items-center justify-center text-white text-sm font-semibold`}>
            {note.author.split(' ').map(n => n[0]).join('')}
          </div>
          <span className="font-medium text-sm flex-1 truncate">{note.author}</span>
          {canDelete && (
            <button
              onClick={() => onDelete(note.id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
              aria-label={`Delete note by ${note.author}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className={`text-foreground ${NOTE_FONT_CLASSES[fontSize]}`}>{note.content}</p>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Board canvas with drop target (must render inside DndProvider)    */
/* ------------------------------------------------------------------ */
function BoardCanvas({
  boardRef,
  notes,
  notesRef,
  loading,
  error,
  isTeacher,
  studentName,
  isPreviewMode,
  getClampedPosition,
  updateNotePosition,
  deleteNote,
  fontSize = 'small',
}: {
  boardRef: React.MutableRefObject<HTMLDivElement | null>;
  notes: StickyNote[];
  notesRef: React.MutableRefObject<StickyNote[]>;
  loading: boolean;
  error: string | null;
  isTeacher: boolean;
  studentName: string | null;
  isPreviewMode: boolean;
  getClampedPosition: (x: number, y: number) => { x: number; y: number };
  updateNotePosition: (noteId: string, pos: { x: number; y: number }) => void;
  deleteNote: (id: string) => void;
  fontSize?: NoteFontSize;
}) {
  const [{ isOver: isBoardOver }, drop] = useDrop<
    { id: string; left: number; top: number },
    unknown,
    { isOver: boolean }
  >({
    accept: 'NOTE',
    canDrop: (item) => {
      const note = notesRef.current.find((n) => n.id === item.id);
      return Boolean(note && (isTeacher || note.author === studentName) && !isPreviewMode);
    },
    drop: (item, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset();
      if (!delta) return;

      const next = getClampedPosition(item.left + delta.x, item.top + delta.y);
      updateNotePosition(item.id, next);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div className="bg-card rounded-2xl border-2 border-border shadow-xl overflow-hidden">
      <div
        ref={(node) => {
          boardRef.current = node;
          drop(node);
        }}
        className={`relative bg-gradient-to-br from-muted/20 to-background touch-none ${
          isBoardOver ? 'ring-2 ring-primary/40' : ''
        }`}
        style={{ minHeight: '600px' }}
      >
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(to right, #E5E7EB 1px, transparent 1px),
              linear-gradient(to bottom, #E5E7EB 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />

        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 text-destructive" />
              <p className="text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : notes.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Plus className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium text-muted-foreground">
                No notes yet. Add your first idea!
              </p>
            </div>
          </div>
        ) : (
          <div className="relative group">
            {notes.map((note) => (
              <DraggableStickyNote
                key={note.id}
                note={note}
                onDelete={deleteNote}
                canDelete={isTeacher || note.author === studentName}
                canDrag={isTeacher || note.author === studentName}
                fontSize={fontSize}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const colors = [
  'bg-yellow-100',
  'bg-pink-100',
  'bg-blue-100',
  'bg-green-100',
  'bg-purple-100',
  'bg-orange-100',
];

const avatarColors = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
];

export default function CollaborateBoard({
  slide,
  onAnswerChange,
  className = '',
  submissions,
}: ActivitySlideProps) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const maxNotesPerStudent = Math.max(1, Number(slide.content?.maxNotes) || 3);
  const { backend, options } = getDndBackend();

  // Identity state
  const [studentName, setStudentName] = useState<string | null>(null);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Board data
  const [notes, setNotes] = useState<StickyNote[]>([]);
  const [participants, setParticipants] = useState<string[]>([]);

  // Font size state – initialize from slide settings if available
  const [noteFontSize, setNoteFontSize] = useState<NoteFontSize>(
    (slide.content?.noteFontSize as NoteFontSize) || 'small'
  );


  // UI state
  const [newNoteContent, setNewNoteContent] = useState('');
  const [moderationMode, setModerationMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs to avoid stale closure issues in async callbacks
  const channelRef = useRef<RealtimeChannel | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notesRef = useRef<StickyNote[]>([]);
  const boardRef = useRef<HTMLDivElement | null>(null);
  // Track the student's own submission ID to avoid a SELECT before INSERT/UPDATE
  const submissionIdRef = useRef<string | null>(null);
  // Map of studentName → submissionId for teacher deletions
  const allSubmissionIdsRef = useRef<Record<string, string>>({});
  // Prevent poll from overwriting optimistic state while a save is in-flight
  const savingRef = useRef(false);

  const NOTE_WIDTH = 256;
  const NOTE_HEIGHT = 180;
  const NOTE_PADDING = 12;

  const studentNoteCount = studentName
    ? notes.filter((n) => n.author === studentName).length
    : 0;
  const isAtNoteLimit = studentNoteCount >= maxNotesPerStudent;

  // Keep notesRef in sync with notes state
  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

  const getBoardBounds = () => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { width: rect.width, height: rect.height };
  };

  const getClampedPosition = (x: number, y: number) => {
    const bounds = getBoardBounds();
    if (!bounds) return { x, y };

    const maxX = Math.max(NOTE_PADDING, bounds.width - NOTE_WIDTH - NOTE_PADDING);
    const maxY = Math.max(NOTE_PADDING, bounds.height - NOTE_HEIGHT - NOTE_PADDING);

    return {
      x: clamp(x, NOTE_PADDING, maxX),
      y: clamp(y, NOTE_PADDING, maxY),
    };
  };

  // Resolve user identity once on mount
  useEffect(() => {
    const studentSession = getStudentSession();
    if (studentSession) {
      setStudentName(studentSession.studentName);
      // Clear any stale Supabase auth session so REST/Realtime calls use the anon key
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) supabase.auth.signOut().catch(() => {});
      });
    } else if (!sessionId) {
      setIsPreviewMode(true);
      setLoading(false);
    } else {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) setIsTeacher(true);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load initial board data from DB
  useEffect(() => {
    // If submissions are provided via props (teacher view), use them directly
    if (submissions && submissions.length > 0) {
      const allNotes: StickyNote[] = submissions.flatMap(
        (s: { answer: any; student_name: string; id: string }) => {
          // Handle multiple possible data shapes:
          // Correct: { notes: [...] }
          // Nested: { answer: { notes: [...] } } (if wrapped)
          const answer = s.answer;
          const notesArray = answer?.notes ?? answer?.answer?.notes ?? [];
          return notesArray as StickyNote[];
        }
      );
      console.log(`🎯 CollaborateBoard: ${submissions.length} submissions received, extracted ${allNotes.length} notes`, submissions.map((s: any) => ({ student: s.student_name, answer: s.answer })));
      setNotes(allNotes);
      notesRef.current = allNotes;
      
      // Build submission ID map
      const idMap: Record<string, string> = {};
      for (const s of submissions) {
        idMap[s.student_name] = s.id;
      }
      allSubmissionIdsRef.current = idMap;
      setLoading(false);
      return;
    }

    // If submissions were provided but are empty, just set empty notes and return
    if (submissions && submissions.length === 0) {
      console.log('🎯 CollaborateBoard: Empty submissions array received');
      setNotes([]);
      notesRef.current = [];
      setLoading(false);
      return;
    }

    if (!sessionId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const isStudent = !!getStudentSession();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Students use server endpoint to bypass RLS; teachers use direct query
        const submissionsData = isStudent
          ? await serverGetSlideSubmissions(sessionId, slide.id)
          : await getSlideSubmissions(sessionId, slide.id);
        if (cancelled) return;

        const allNotes: StickyNote[] = submissionsData.flatMap(
          (s: { answer: any }) => {
            const answer = s.answer;
            return (answer?.notes ?? answer?.answer?.notes ?? []) as StickyNote[];
          }
        );
        setNotes(allNotes);
        notesRef.current = allNotes;

        // Capture existing submission ID for the current student so later
        // saves can UPDATE directly instead of doing a SELECT first
        const currentStudent = getStudentSession();
        // Build full map of studentName → submissionId (used for teacher deletions too)
        const idMap: Record<string, string> = {};
        for (const s of submissionsData) {
          idMap[s.student_name] = s.id;
        }
        allSubmissionIdsRef.current = idMap;
        if (currentStudent && idMap[currentStudent.studentName]) {
          submissionIdRef.current = idMap[currentStudent.studentName];
        }
      } catch (err) {
        if (!cancelled) {
          console.error('CollaborateBoard: failed to load initial data', err);
          setError('Failed to load board data. Please refresh.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Fetch participants separately — failure should not block notes
      try {
        const participantsData = await getSessionParticipants(sessionId);
        if (!cancelled) {
          setParticipants(participantsData.map((p) => p.student_name));
        }
      } catch {
        // Non-critical — participant list may be empty for students due to RLS
      }
    })();

    return () => { cancelled = true; };
  }, [sessionId, slide.id, submissions]);

  // Poll every 5s as reliability fallback for missed broadcasts
  useEffect(() => {
    // Skip polling if submissions are provided via props (teacher view handles its own refresh)
    if (!sessionId || submissions) return;

    const isStudent = !!getStudentSession();
    const currentStudent = getStudentSession();

    const poll = async () => {
      // Skip if a save is in-flight to avoid overwriting optimistic state
      if (savingRef.current) return;

      // Fetch submissions independently — this is the critical path for notes
      try {
        const fetchedSubmissions = isStudent
          ? await serverGetSlideSubmissions(sessionId, slide.id)
          : await getSlideSubmissions(sessionId, slide.id);
        
        // Extract notes from all submissions
        const fetchedNotes: StickyNote[] = fetchedSubmissions.flatMap(
          (s: { answer: { notes: any; }; }) => (s.answer?.notes ?? []) as StickyNote[]
        );

        // Merge strategy: keep current student's notes (optimistic), sync other students' notes
        const currentStudentName = currentStudent?.studentName;

        if (currentStudentName) {
          // Keep this student's notes from local state (optimistic updates)
          const myLocalNotes = notesRef.current.filter((n) => n.author === currentStudentName);
          
          // Get other students' notes from polling
          const otherStudentsNotes = fetchedNotes.filter((n) => n.author !== currentStudentName);
          
          // Combine: my local notes + other students' notes from server
          const merged = [...otherStudentsNotes, ...myLocalNotes];
          setNotes(merged);
          notesRef.current = merged;
        } else {
          // Not a student (shouldn't happen in normal flow, but fallback to server data)
          setNotes(fetchedNotes);
          notesRef.current = fetchedNotes;
        }

        // Refresh submission ID map
        const idMap: Record<string, string> = {};
        for (const s of fetchedSubmissions) idMap[s.student_name] = s.id;
        allSubmissionIdsRef.current = idMap;
        if (currentStudent && idMap[currentStudent.studentName]) {
          submissionIdRef.current = idMap[currentStudent.studentName];
        }
      } catch {
        // Silent failure for submissions — notes stay as-is
      }

      // Fetch participants separately so its failure doesn't block notes
      try {
        const participantsData = await getSessionParticipants(sessionId);
        setParticipants(participantsData.map((p) => p.student_name));
      } catch {
        // Non-critical — participant list may be empty for students due to RLS
      }
    };

    pollingIntervalRef.current = setInterval(poll, 5000);
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [sessionId, slide.id, submissions]);

  // Subscribe to dedicated broadcast channel for this board
  useEffect(() => {
    // Skip real-time subscription if submissions are provided via props (teacher view)
    if (!sessionId || submissions) return;

    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let activeChannel: ReturnType<typeof supabase.channel> | null = null;

    const setupChannel = () => {
      if (cancelled) return;

      const channelName = `collab_${sessionId}_${slide.id}`;
      // Remove any stale channel with same name before creating a new one
      if (activeChannel) {
        supabase.removeChannel(activeChannel).catch(() => {});
        activeChannel = null;
      }

      const channel = supabase.channel(channelName, {
        config: { broadcast: { self: false } },
      });

      channel.on('broadcast', { event: 'collaborate_note_added' }, ({ payload }) => {
        const incoming = (payload as CollaborateNoteAddedPayload).note;
        if (!incoming) return;
        setNotes((prev) => {
          if (prev.some((n) => n.id === incoming.id)) return prev;
          const updated = [...prev, incoming];
          notesRef.current = updated;
          return updated;
        });
      });

      channel.on('broadcast', { event: 'collaborate_note_deleted' }, ({ payload }) => {
        const { noteId } = payload as CollaborateNoteDeletedPayload;
        if (!noteId) return;
        setNotes((prev) => {
          const updated = prev.filter((n) => n.id !== noteId);
          notesRef.current = updated;
          return updated;
        });
      });

      channel.on('broadcast', { event: 'collaborate_note_moved' }, ({ payload }) => {
        const { noteId, position } = payload as CollaborateNoteMovedPayload;
        if (!noteId || !position) return;
        setNotes((prev) => {
          const updated = prev.map((note) =>
            note.id === noteId ? { ...note, position: getClampedPosition(position.x, position.y) } : note
          );
          notesRef.current = updated;
          return updated;
        });
      });

      channel.subscribe((status) => {
        if (cancelled) return;
        if (status === 'SUBSCRIBED') {
          // Successfully connected
          channelRef.current = channel;
          activeChannel = channel;
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Retry after a delay — polling keeps notes in sync meanwhile
          console.warn(`CollaborateBoard: channel ${status}, retrying in 5s…`);
          retryTimeout = setTimeout(() => {
            if (!cancelled) setupChannel();
          }, 5000);
        }
      });

      activeChannel = channel;
    };

    setupChannel();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (activeChannel) {
        unsubscribeFromSession(activeChannel).catch(console.error);
      }
      channelRef.current = null;
    };
  }, [sessionId, slide.id, submissions]);

  const addNote = useCallback(async () => {
    if (!newNoteContent.trim()) return;

    if (!studentName) {
      toast.error(isPreviewMode ? 'Preview mode: notes are not saved' : 'Only students can add notes');
      return;
    }

    const currentStudentNotes = notesRef.current.filter((n) => n.author === studentName);
    if (currentStudentNotes.length >= maxNotesPerStudent) {
      toast.error(`You can only add ${maxNotesPerStudent} notes for this activity`);
      return;
    }

    if (!sessionId) return;

    const noteId = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const bounds = getBoardBounds();
    const maxX = bounds ? Math.max(NOTE_PADDING, bounds.width - NOTE_WIDTH - NOTE_PADDING) : 500;
    const maxY = bounds ? Math.max(NOTE_PADDING, bounds.height - NOTE_HEIGHT - NOTE_PADDING) : 300;
    const randomPosition = getClampedPosition(
      Math.random() * maxX,
      Math.random() * maxY
    );

    const newNote: StickyNote = {
      id: noteId,
      content: newNoteContent.trim(),
      author: studentName,
      color: colors[Math.floor(Math.random() * colors.length)],
      position: randomPosition,
      avatar: avatarColors[Math.floor(Math.random() * avatarColors.length)],
    };

    // Optimistic update
    setNotes((prev) => {
      const updated = [...prev, newNote];
      notesRef.current = updated;
      return updated;
    });
    setNewNoteContent('');
    onAnswerChange({ noteCount: notesRef.current.filter((n) => n.author === studentName).length });

    try {
      savingRef.current = true;
      const myNotes = notesRef.current.filter((n) => n.author === studentName);
      const answerPayload = { notes: myNotes };

      const submissionId = submissionIdRef.current || crypto.randomUUID();
      const result = await serverUpsertSubmission({
        id: submissionId,
        session_id: sessionId,
        slide_id: slide.id,
        student_name: studentName,
        answer: answerPayload,
      });
      if (!submissionIdRef.current) {
        submissionIdRef.current = result.id || submissionId;
        allSubmissionIdsRef.current[studentName] = submissionIdRef.current;
      }
    } catch (err: any) {
      const msg = err?.message || err?.details || JSON.stringify(err);
      console.error('CollaborateBoard: failed to save note', { code: err?.code, message: msg, details: err?.details, hint: err?.hint });
      toast.error('Failed to save your note. Please try again.');
      // Rollback optimistic update and restore input
      setNotes((prev) => {
        const rolled = prev.filter((n) => n.id !== noteId);
        notesRef.current = rolled;
        return rolled;
      });
      setNewNoteContent(newNote.content);
    } finally {
      savingRef.current = false;
    }

    // Broadcast is best-effort and must not roll back a successful save
    if (channelRef.current) {
      broadcastToSession(channelRef.current, 'collaborate_note_added', { note: newNote } as CollaborateNoteAddedPayload).catch(() => {});
    }
  }, [newNoteContent, studentName, sessionId, slide.id, isPreviewMode, onAnswerChange, maxNotesPerStudent]);

  const updateNotePosition = useCallback(async (noteId: string, nextPosition: { x: number; y: number }) => {
    const target = notesRef.current.find((n) => n.id === noteId);
    if (!target) return;

    if (!isTeacher && target.author !== studentName) return;
    if (isPreviewMode) return;

    const updatedNotes = notesRef.current.map((note) =>
      note.id === noteId
        ? { ...note, position: getClampedPosition(nextPosition.x, nextPosition.y) }
        : note
    );

    setNotes(updatedNotes);
    notesRef.current = updatedNotes;

    if (!sessionId) return;

    try {
      savingRef.current = true;
      const authorName = target.author;
      const authorNotes = updatedNotes.filter((n) => n.author === authorName);
      const answerPayload = { notes: authorNotes };

      const existingId = allSubmissionIdsRef.current[authorName];
      const fallbackId = crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const submissionId = existingId || fallbackId;

      await serverUpsertSubmission({
        id: submissionId,
        session_id: sessionId,
        slide_id: slide.id,
        student_name: authorName,
        answer: answerPayload,
      });

      if (!existingId) allSubmissionIdsRef.current[authorName] = submissionId;
      if (authorName === studentName && !submissionIdRef.current) {
        submissionIdRef.current = submissionId;
      }
    } catch (err: any) {
      const msg = err?.message || err?.details || JSON.stringify(err);
      console.error('CollaborateBoard: failed to move note', { code: err?.code, message: msg });
      toast.error('Failed to move note. Please try again.');
    } finally {
      savingRef.current = false;
    }

    if (channelRef.current) {
      broadcastToSession(channelRef.current, 'collaborate_note_moved', {
        noteId,
        position: nextPosition,
        authorStudentName: target.author,
      } as CollaborateNoteMovedPayload).catch(() => {});
    }
  }, [getClampedPosition, isPreviewMode, isTeacher, sessionId, slide.id, studentName]);

  const deleteNote = useCallback(async (id: string) => {
    const noteToDelete = notesRef.current.find((n) => n.id === id);
    if (!noteToDelete) return;

    if (!isTeacher && noteToDelete.author !== studentName) {
      toast.error('You can only delete your own notes');
      return;
    }

    const previousNotes = notesRef.current;
    setNotes((prev) => {
      const updated = prev.filter((n) => n.id !== id);
      notesRef.current = updated;
      return updated;
    });

    if (!sessionId) return; // Preview mode: local-only deletion

    try {
      savingRef.current = true;
      const authorName = noteToDelete.author;
      const remaining = notesRef.current.filter((n) => n.author === authorName);
      const answerPayload = { notes: remaining };
      const authorSubmissionId = allSubmissionIdsRef.current[authorName];

      if (authorSubmissionId) {
        // Update the author's submission via server to bypass RLS
        await serverUpsertSubmission({
          id: authorSubmissionId,
          session_id: sessionId,
          slide_id: slide.id,
          student_name: authorName,
          answer: answerPayload,
        });
      }
      // If no submission ID found (edge case), skip DB update — polling will reconcile

      // Broadcast is best-effort and must not roll back a successful delete
      if (channelRef.current) {
        broadcastToSession(channelRef.current, 'collaborate_note_deleted', {
          noteId: id,
          authorStudentName: authorName,
        } as CollaborateNoteDeletedPayload).catch(() => {});
      }
    } catch (err: any) {
      const msg = err?.message || err?.details || JSON.stringify(err);
      console.error('CollaborateBoard: failed to delete note', { code: err?.code, message: msg });
      toast.error('Failed to delete note. Please try again.');
      setNotes(() => {
        notesRef.current = previousNotes;
        return previousNotes;
      });
    } finally {
      savingRef.current = false;
    }
  }, [studentName, isTeacher, sessionId, slide.id]);

  // ── Teacher Live View Dashboard ──
  // When submissions are provided via props, render a Nearpod-style teacher board
  // showing all submitted notes in real-time with moderation controls

  // Subscribe teacher to collab broadcast channel for instant note updates
  // Use a stable check (not `submissions` reference) to avoid tearing down the channel on every poll
  const isTeacherView = !!submissions;
  useEffect(() => {
    if (!isTeacherView || !sessionId) return;

    let cancelled = false;
    let activeChannel: ReturnType<typeof supabase.channel> | null = null;

    const channelName = `collab_${sessionId}_${slide.id}`;
    const channel = supabase.channel(channelName, {
      config: { broadcast: { self: false } },
    });

    channel.on('broadcast', { event: 'collaborate_note_added' }, ({ payload }) => {
      const incoming = (payload as CollaborateNoteAddedPayload).note;
      if (!incoming) return;
      setNotes((prev) => {
        if (prev.some((n) => n.id === incoming.id)) return prev;
        const updated = [...prev, incoming];
        notesRef.current = updated;
        return updated;
      });
    });

    channel.on('broadcast', { event: 'collaborate_note_deleted' }, ({ payload }) => {
      const { noteId } = payload as CollaborateNoteDeletedPayload;
      if (!noteId) return;
      setNotes((prev) => {
        const updated = prev.filter((n) => n.id !== noteId);
        notesRef.current = updated;
        return updated;
      });
    });

    channel.on('broadcast', { event: 'collaborate_note_moved' }, ({ payload }) => {
      const { noteId, position } = payload as CollaborateNoteMovedPayload;
      if (!noteId || !position) return;
      setNotes((prev) => {
        const updated = prev.map((note) =>
          note.id === noteId ? { ...note, position } : note
        );
        notesRef.current = updated;
        return updated;
      });
    });

    channel.subscribe((status) => {
      if (cancelled) return;
      if (status === 'SUBSCRIBED') {
        activeChannel = channel;
        channelRef.current = channel;
        console.log('✅ Teacher subscribed to collab broadcast channel');
      }
    });

    activeChannel = channel;

    return () => {
      cancelled = true;
      if (activeChannel) {
        unsubscribeFromSession(activeChannel).catch(console.error);
      }
      channelRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacherView, sessionId, slide.id]);

  // Teacher: delete a note and persist via server endpoint
  const teacherDeleteNote = useCallback(async (noteId: string) => {
    const noteToDelete = notesRef.current.find((n) => n.id === noteId);
    if (!noteToDelete || !sessionId) return;

    const previousNotes = notesRef.current;
    setNotes((prev) => {
      const updated = prev.filter((n) => n.id !== noteId);
      notesRef.current = updated;
      return updated;
    });

    try {
      const authorName = noteToDelete.author;
      const remaining = notesRef.current.filter((n) => n.author === authorName);
      const authorSubmissionId = allSubmissionIdsRef.current[authorName];

      if (authorSubmissionId) {
        await serverUpsertSubmission({
          id: authorSubmissionId,
          session_id: sessionId,
          slide_id: slide.id,
          student_name: authorName,
          answer: { notes: remaining },
        });
      }

      // Broadcast deletion to students
      if (channelRef.current) {
        broadcastToSession(channelRef.current, 'collaborate_note_deleted', {
          noteId,
          authorStudentName: authorName,
        } as CollaborateNoteDeletedPayload).catch(() => {});
      }

      toast.success('Note deleted');
    } catch (err: any) {
      console.error('Teacher: failed to delete note', err);
      toast.error('Failed to delete note');
      setNotes(() => {
        notesRef.current = previousNotes;
        return previousNotes;
      });
    }
  }, [sessionId, slide.id]);

  if (submissions) {
    // Compute per-student note counts
    const notesByAuthor: Record<string, StickyNote[]> = {};
    for (const note of notes) {
      if (!notesByAuthor[note.author]) notesByAuthor[note.author] = [];
      notesByAuthor[note.author].push(note);
    }
    const contributorNames = Object.keys(notesByAuthor);
    const contributorCount = contributorNames.length;

    return (
      <DndProvider backend={backend} options={options}>
        <div className={`w-full ${className}`}>
          <style>{RICH_TEXT_STYLES}</style>
          <div className="bg-gradient-to-br from-emerald-500/5 via-background to-accent/5 rounded-3xl p-5 sm:p-6 border border-border/40">
          {/* Header */}
          <div className="text-center mb-5">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-600 px-4 py-1.5 rounded-full mb-2">
              <StickyNote className="w-4 h-4" />
              <span className="text-sm font-bold">Collaborate Board</span>
            </div>
            {slide.content?.showTitle !== false && slide.content?.title && slide.content.title !== 'Collaborate Board' && (
              <h2 className="text-lg font-bold mb-1">{stripHtml(slide.content.title)}</h2>
            )}
            {slide.content?.prompt ? (
              <div className="text-sm text-muted-foreground rich-text-content" dangerouslySetInnerHTML={{ __html: slide.content.prompt }} />
            ) : (
              <p className="text-sm text-muted-foreground">Student ideas and notes</p>
            )}
          </div>

          {/* Font Size Toggle - Teacher View */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-xs text-muted-foreground font-medium">Text Size:</span>
            <div className="flex items-center gap-1 bg-white/80 dark:bg-card/80 rounded-xl border border-border/40 p-0.5">
              <button
                onClick={() => setNoteFontSize('small')}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                  noteFontSize === 'small'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                title="Small text"
              >
                <AArrowUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => setNoteFontSize('medium')}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                  noteFontSize === 'medium'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                title="Medium text"
              >
                <AArrowUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => setNoteFontSize('large')}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                  noteFontSize === 'large'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                title="Large text"
              >
                <AArrowUp className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mb-5 max-w-md mx-auto">
            <div className="bg-card/60 backdrop-blur-sm rounded-xl p-3 border border-border/40 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <StickyNote className="w-3.5 h-3.5 text-primary" />
                <span className="text-[10px] text-muted-foreground">Notes</span>
              </div>
              <p className="text-xl font-extrabold text-primary">{notes.length}</p>
            </div>
            <div className="bg-card/60 backdrop-blur-sm rounded-xl p-3 border border-border/40 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <Users className="w-3.5 h-3.5 text-accent" />
                <span className="text-[10px] text-muted-foreground">Contributors</span>
              </div>
              <p className="text-xl font-extrabold text-accent">{contributorCount}</p>
            </div>
            <div className="bg-card/60 backdrop-blur-sm rounded-xl p-3 border border-border/40 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <MessageSquare className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-[10px] text-muted-foreground">Submissions</span>
              </div>
              <p className="text-xl font-extrabold text-orange-500">{submissions.length}</p>
            </div>
          </div>

          {/* Board Canvas — Nearpod-style positioned sticky notes */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-10 bg-card/60 backdrop-blur-sm rounded-2xl border-2 border-dashed border-border/40">
              <StickyNote className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-base font-medium text-muted-foreground">No notes submitted yet</p>
              <p className="text-xs text-muted-foreground mt-1">Notes appear here in real-time</p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="text-xs text-accent font-medium">Waiting for students...</span>
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <StickyNote className="w-4 h-4" />
                  Board ({notes.length} notes)
                </h3>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                  <span className="text-xs text-accent font-medium">Live</span>
                </div>
              </div>

              {/* Board Canvas with positioned notes */}
              <div
                ref={boardRef}
                className="relative bg-card rounded-2xl border-2 border-border shadow-xl overflow-hidden"
                style={{ minHeight: '500px' }}
              >
                {/* Grid Pattern */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, #E5E7EB 1px, transparent 1px),
                      linear-gradient(to bottom, #E5E7EB 1px, transparent 1px)
                    `,
                    backgroundSize: '40px 40px',
                  }}
                />
                {notes.map((note) => (
                  <motion.div
                    key={note.id}
                    initial={{ scale: 0, rotate: -5 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    style={{
                      position: 'absolute',
                      left: note.position?.x ?? 20,
                      top: note.position?.y ?? 20,
                    }}
                    className="group"
                  >
                    <div
                      className={`w-56 p-4 rounded-2xl shadow-lg border-2 border-border hover:shadow-xl transition-shadow ${note.color}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-7 h-7 rounded-full ${note.avatar} flex items-center justify-center text-white text-xs font-semibold`}>
                          {note.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-xs font-medium text-muted-foreground truncate flex-1">{note.author}</span>
                        <button
                          onClick={() => teacherDeleteNote(note.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                          aria-label={`Delete note by ${note.author}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className={`text-foreground ${NOTE_FONT_CLASSES[noteFontSize]}`}>{note.content}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Notes List (compact summary below the board) */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[250px] overflow-auto pr-1">
                {notes.map((note) => (
                  <div
                    key={`list-${note.id}`}
                    className={`p-3 rounded-xl border border-border shadow-sm flex items-start gap-2 ${note.color}`}
                  >
                    <div className={`w-6 h-6 rounded-full ${note.avatar} flex-shrink-0 flex items-center justify-center text-white text-[10px] font-semibold mt-0.5`}>
                      {note.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-muted-foreground block truncate">{note.author}</span>
                      <p className={`text-foreground mt-0.5 ${NOTE_FONT_CLASSES[noteFontSize]}`}>{note.content}</p>
                    </div>
                    <button
                      onClick={() => teacherDeleteNote(note.id)}
                      className="text-muted-foreground hover:text-destructive flex-shrink-0"
                      aria-label={`Delete note by ${note.author}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Participants with Note Counts */}
          {contributorCount > 0 && (
            <div>
              <h3 className="font-semibold mb-3 text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                Contributors ({contributorCount})
              </h3>
              <div className="flex flex-wrap gap-2">
                {contributorNames
                  .sort((a, b) => (notesByAuthor[b]?.length || 0) - (notesByAuthor[a]?.length || 0))
                  .map((name, i) => (
                  <div
                    key={name}
                    className="flex items-center gap-2 px-3 py-2 bg-card rounded-xl border border-border"
                  >
                    <div className={`w-7 h-7 rounded-full ${avatarColors[i % avatarColors.length]} flex items-center justify-center text-white text-xs font-semibold`}>
                      {name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <span className="text-sm font-medium">{name}</span>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {notesByAuthor[name].length} {notesByAuthor[name].length === 1 ? 'note' : 'notes'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </DndProvider>
    );
  }

  return (
    <DndProvider backend={backend} options={options}>
      <div className={`w-full ${className}`}>
        <style>{RICH_TEXT_STYLES}</style>
        <div className="bg-gradient-to-br from-emerald-500/5 via-background to-accent/5 rounded-3xl p-5 sm:p-6 border border-border/40">
          {/* Top Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-600 px-3 py-1 rounded-full mb-1">
                <StickyNote className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">Collaborate Board</span>
              </div>
              {slide.content?.showTitle !== false && (
                <h2 className="text-lg font-bold">{stripHtml(slide.content?.title) || 'Collaborate Board'}</h2>
              )}
              {slide.content?.prompt ? (
                <div className="text-sm text-muted-foreground rich-text-content" dangerouslySetInnerHTML={{ __html: slide.content.prompt }} />
              ) : (
                <p className="text-sm text-muted-foreground">Share your ideas on this board</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Font Size Toggle */}
              <div className="flex items-center gap-1 bg-white/80 dark:bg-card/80 rounded-xl border border-border/40 p-0.5">
                <button
                  onClick={() => setNoteFontSize('small')}
                  className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                    noteFontSize === 'small'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  title="Small text"
                >
                  <AArrowUp className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setNoteFontSize('medium')}
                  className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                    noteFontSize === 'medium'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  title="Medium text"
                >
                  <AArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setNoteFontSize('large')}
                  className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                    noteFontSize === 'large'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  title="Large text"
                >
                  <AArrowUp className="w-5 h-5" />
                </button>
              </div>
              {isTeacher && (
                <Button
                  variant={moderationMode ? 'default' : 'outline'}
                  onClick={() => setModerationMode(!moderationMode)}
                  className="rounded-xl text-sm"
                  size="sm"
                >
                  {moderationMode ? (
                    <>
                      <Eye className="w-4 h-4 mr-1.5" />
                      Moderation ON
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4 mr-1.5" />
                      Moderation OFF
                    </>
                  )}
                </Button>
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-full">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="font-bold text-accent text-sm">{notes.length} notes</span>
              </div>
            </div>
          </div>

        <div className="space-y-5">
            {/* Add Note Section */}
            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3" id="add-idea-label">Add Your Idea</h3>
              <div className="flex gap-2">
                <Input
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  placeholder={
                    isPreviewMode
                      ? 'Preview mode — join a session to add notes'
                      : !studentName
                      ? 'Only students can add notes'
                      : isAtNoteLimit
                      ? 'Note limit reached'
                      : 'Type your idea here...'
                  }
                  className="rounded-xl border-border/40 focus:ring-2 focus:ring-primary/30"
                  onKeyDown={(e) => e.key === 'Enter' && addNote()}
                  disabled={!studentName || isPreviewMode || isAtNoteLimit}
                  aria-labelledby="add-idea-label"
                />
                <Button
                  onClick={addNote}
                  disabled={!studentName || isPreviewMode || isAtNoteLimit}
                  className="rounded-xl flex-shrink-0 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              {studentName && !isPreviewMode && (
                <p className="mt-1.5 text-[10px] text-muted-foreground">
                  {Math.max(0, maxNotesPerStudent - studentNoteCount)} notes remaining
                </p>
              )}
            </div>

            {/* Collaborate Board Canvas */}
            <BoardCanvas
              boardRef={boardRef}
              notes={notes}
              notesRef={notesRef}
              loading={loading}
              error={error}
              isTeacher={isTeacher}
              studentName={studentName}
              isPreviewMode={isPreviewMode}
              getClampedPosition={getClampedPosition}
              updateNotePosition={updateNotePosition}
              deleteNote={deleteNote}
              fontSize={noteFontSize}
            />

            {/* Participants */}
            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Active Participants
              </h3>
              <div className="flex flex-wrap gap-2">
                {participants.map((name, i) => (
                  <div
                    key={name}
                    className="flex items-center gap-2 px-3 py-1.5 bg-background rounded-xl border border-border/40"
                  >
                    <div className={`w-6 h-6 rounded-full ${avatarColors[i % avatarColors.length]} flex items-center justify-center text-white text-[10px] font-semibold`}>
                      {name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <span className="text-sm font-medium">
                      {name === studentName ? `${name} (You)` : name}
                    </span>
                    <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  </div>
                ))}
                {participants.length === 0 && !loading && (
                  <p className="text-xs text-muted-foreground italic">
                    {isPreviewMode
                      ? 'Preview mode — participants appear during live sessions.'
                      : 'No participants yet.'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
